import { Link } from "react-router-dom";
import {
  Gauge,
  Wrench,
  Anchor,
  Waves,
  Shield,
  Droplets,
  Beaker,
  ClipboardList,
  ArrowRight,
  Compass,
  AlertTriangle,
  Library,
  ClipboardCheck,
  Wrench as WrenchIcon,
  FlaskConical,
} from "lucide-react";

const CATS = [
  {
    to: "/pressure-control",
    icon: Gauge,
    title: "Pressure Control",
    lines: ["Kill Mud Weight", "ICP / FCP", "MAASP", "Formation Pressure"],
  },
  {
    to: "/kill-sheet",
    icon: ClipboardList,
    title: "Kill Sheet",
    lines: ["Wait & Weight worksheet", "Strokes to bit / surface", "Pressure step-down"],
  },
  {
    to: "/kick-tolerance",
    icon: AlertTriangle,
    title: "Kick Tolerance",
    lines: ["Max influx before frac", "GO / CAUTION / STOP verdict"],
  },
  {
    to: "/directional",
    icon: Compass,
    title: "Directional Drilling",
    lines: ["Dogleg Severity", "Minimum Curvature Method"],
  },
  {
    to: "/tubing-movement",
    icon: Wrench,
    title: "Tubing Movement",
    lines: ["Hooke's law", "Piston effect", "Ballooning", "Thermal"],
  },
  {
    to: "/free-point",
    icon: Anchor,
    title: "Free Point",
    lines: ["Stretch method", "Stuck-pipe depth"],
  },
  {
    to: "/fishing",
    icon: WrenchIcon,
    title: "Fishing",
    lines: ["Pipe body yield", "Safe pull / overpull", "Jar impact energy"],
  },
  {
    to: "/collapse-burst",
    icon: Shield,
    title: "Collapse / Burst",
    lines: ["API 5C3 yield collapse", "Barlow burst"],
  },
  {
    to: "/hydrostatic",
    icon: Waves,
    title: "Hydrostatic",
    lines: ["HP from MW × TVD", "Formation strength"],
  },
  {
    to: "/capacities",
    icon: Beaker,
    title: "Capacities & Volumes",
    lines: ["Pipe / Annular capacity", "Displacement strokes"],
  },
  {
    to: "/mud-weight",
    icon: Droplets,
    title: "Mud Weight",
    lines: ["Weight up (barite)", "Weight down (dilution)"],
  },
  {
    to: "/cementing",
    icon: FlaskConical,
    title: "Cementing Volumes",
    lines: ["Slurry, sacks, mix water", "Displacement + strokes", "Slurry density"],
  },
  {
    to: "/casing-wizard",
    icon: ClipboardCheck,
    title: "Casing Design Wizard",
    lines: ["Setpoints → Loads → Pipe pick", "Auto-verify with SF, PASS/FAIL"],
  },
  {
    to: "/specs",
    icon: Library,
    title: "Spec Library",
    lines: ["Casing / Tubing / DP", "API 5CT standard sizes"],
  },
];

export default function Overview() {
  return (
    <div data-testid="overview-page">
      <div className="mb-8">
        <div className="text-xs mono tracking-widest text-[#f59e0b] mb-2">
          // FIELD OPERATIONS TERMINAL
        </div>
        <h1 className="heading text-4xl font-black leading-tight">
          Downhole formulas,
          <br />
          <span className="text-[#f59e0b]">field-ready.</span>
        </h1>
        <p className="text-[#94a3b8] mt-3 max-w-2xl">
          Pressure control, tubing mechanics, free point, collapse/burst, and mud
          weight — every calculation calibrated to real-world well control
          practice. Switch between Field and Metric units, save jobs, and ask the
          AI assistant for a walkthrough.
        </p>
        <Link to="/tools" data-testid="overview-guides-link" className="inline-flex items-center gap-1 mt-4 mono text-xs tracking-widest uppercase text-[#f59e0b] hover:text-[#fbbf24]">
          Browse free formula guides <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {CATS.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`overview-card-${c.to.slice(1)}`}
              className="group border border-[#334155] bg-[#11151c] p-5 hover:border-[#f59e0b] transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className="w-6 h-6 text-[#f59e0b]" />
                <ArrowRight className="w-4 h-4 text-[#334155] group-hover:text-[#f59e0b] transition-colors" />
              </div>
              <h3 className="heading font-bold text-lg mb-2">{c.title}</h3>
              <ul className="space-y-1">
                {c.lines.map((l) => (
                  <li key={l} className="text-xs mono text-[#94a3b8]">
                    · {l}
                  </li>
                ))}
              </ul>
            </Link>
          );
        })}
      </div>

      <div className="mt-10 border border-[#334155] bg-[#11151c] p-5">
        <div className="text-xs mono tracking-widest text-[#f59e0b] mb-2">
          SAFETY NOTICE
        </div>
        <p className="text-sm text-[#94a3b8] leading-relaxed">
          These calculators support field decisions but do not replace the well
          control organisation on site. Always cross-check against the well
          program, IWCF/IADC procedures, and current company standards before
          executing a job.
        </p>
      </div>
    </div>
  );
}
